export type AppMode = "demo" | "authenticated";

const DEMO_STORAGE_KEY = "bitetrack-demo";
const DEMO_EXIT_OVERRIDE_STORAGE_KEY = "bitetrack-demo-exit-override";
export const DEMO_INSTALL_MESSAGE = "This is a demo mode. Install the app to use it.";

export type DemoModeLocation = {
  readonly search: string;
  readonly hash: string;
  readonly storedValue?: string | null;
  readonly exitOverride?: boolean;
};

export function shouldUseDemoMode(location: DemoModeLocation): boolean {
  const params = new URLSearchParams(location.search);
  const hash = location.hash.replace(/^#/, "");
  if (params.get("demo") === "1" || hash === "demo") return true;
  return location.exitOverride !== true && location.storedValue === "true";
}

export function normalizeInstallUrl(value: string | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  try {
    const url = new URL(trimmed);
    return url.protocol === "https:" ? url.toString() : null;
  } catch (error: unknown) {
    console.warn("[Frontend][AppMode] Install URL is unavailable because it is invalid", {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

export function getDemoInstallUrl(): string | null {
  return null;
}

export function isDemoModeRequested(): boolean {
  if (typeof window === "undefined") return false;

  try {
    if (window.localStorage.getItem(DEMO_STORAGE_KEY) !== null) {
      window.localStorage.removeItem(DEMO_STORAGE_KEY);
    }
    return shouldUseDemoMode({
      search: window.location.search,
      hash: window.location.hash,
      exitOverride: readDemoModeExitOverride(),
    });
  } catch (error: unknown) {
    console.warn("[Frontend][AppMode] Demo preference could not be read", {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}

export function readDemoModeExitOverride(): boolean {
  if (typeof window === "undefined") return false;

  try {
    const localOverride = window.localStorage.getItem(DEMO_EXIT_OVERRIDE_STORAGE_KEY) === "true";
    const sessionOverride = window.sessionStorage.getItem(DEMO_EXIT_OVERRIDE_STORAGE_KEY) === "true";
    if (sessionOverride && !localOverride) {
      window.localStorage.setItem(DEMO_EXIT_OVERRIDE_STORAGE_KEY, "true");
    }
    return localOverride || sessionOverride;
  } catch (error: unknown) {
    console.warn("[Frontend][AppMode] Demo exit override could not be read", {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}

export function persistDemoModeExitOverride(): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(DEMO_EXIT_OVERRIDE_STORAGE_KEY, "true");
    window.sessionStorage.setItem(DEMO_EXIT_OVERRIDE_STORAGE_KEY, "true");
  } catch (error: unknown) {
    console.warn("[Frontend][AppMode] Demo exit override could not be persisted", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export function clearDemoModeExitOverride(): void {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.removeItem(DEMO_EXIT_OVERRIDE_STORAGE_KEY);
    window.localStorage.removeItem(DEMO_EXIT_OVERRIDE_STORAGE_KEY);
  } catch (error: unknown) {
    console.warn("[Frontend][AppMode] Demo exit override could not be cleared", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export function persistDemoMode(enabled: boolean): void {
  if (typeof window === "undefined") return;

  try {
    if (enabled) window.localStorage.setItem(DEMO_STORAGE_KEY, "true");
    else window.localStorage.removeItem(DEMO_STORAGE_KEY);
  } catch (error: unknown) {
    console.warn("[Frontend][AppMode] Demo preference could not be persisted", {
      enabled,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export function clearDemoModeRequest(): void {
  if (typeof window === "undefined") return;

  persistDemoMode(false);
  const url = new URL(window.location.href);
  url.searchParams.delete("demo");
  if (url.hash.replace(/^#/, "") === "demo") url.hash = "";
  window.history.replaceState(null, "", url);
}
